// src/pages/shop/components/MessagesList.js - UPDATED with better chat selection highlighting
import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { Package, Trash2, Clock, Check, AlertCircle, User, CheckSquare, Square } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../../firebase/config';

const ChatItemsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(128, 0, 0, 0.5);
    border-radius: 10px;
  }
`;

const ChatItem = styled.div`
  display: flex;
  gap: 1rem;
  background: ${props => props.active ? 
    'linear-gradient(135deg, rgba(128, 0, 0, 0.4), rgba(128, 0, 0, 0.25))' : 
    'rgba(0, 0, 0, 0.4)'
  };
  border: 2px solid ${props => {
    if (props.active) return '#800000';
    if (props.isBuyer) return 'rgba(255, 193, 7, 0.6)';
    if (props.isSeller) return 'rgba(33, 150, 243, 0.6)';
    return 'rgba(128, 0, 0, 0.3)';
  }};
  border-radius: 12px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  font-family: ${props => props.theme?.fonts?.body || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'};
  
  /* ✅ NEW: Active state styling */
  ${props => props.active && `
    box-shadow: 0 0 0 3px rgba(128, 0, 0, 0.2),
                0 5px 20px rgba(128, 0, 0, 0.3);
    transform: translateX(4px);
    
    .chat-name {
      color: #FFFFFF !important;
      font-weight: 700 !important;
    }
    
    .chat-preview {
      opacity: 1 !important;
      color: rgba(255, 255, 255, 0.95) !important;
    }
  `}
  
  &:hover {
    transform: translateY(-2px);
    border-color: ${props => {
      if (props.active) return '#A00000';
      if (props.isBuyer) return '#FFC107';
      if (props.isSeller) return '#2196F3';
      return '#800000';
    }};
    box-shadow: ${props => props.active ? 
      '0 0 0 3px rgba(128, 0, 0, 0.3), 0 8px 25px rgba(128, 0, 0, 0.4)' :
      '0 5px 15px rgba(0, 0, 0, 0.2)'
    };
    
    .delete-btn {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  .chat-avatar {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    background: rgba(128, 0, 0, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    border: 2px solid ${props => {
      if (props.active) return 'rgba(128, 0, 0, 0.8)';
      if (props.isBuyer) return 'rgba(255, 193, 7, 0.4)';
      return 'rgba(33, 150, 243, 0.4)';
    }};
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    
    svg {
      opacity: 0.5;
      color: rgba(255, 255, 255, 0.6);
    }
  }
  

  .status-indicator {
    position: absolute;
    top: -2px;
    left: -2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid ${props => props.theme?.colors?.background || '#0B0B3B'};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    
    &.active {
      background: #FFC107;
      color: #000;
    }
    
    &.completed {
      background: #4CAF50;
      color: #fff;
    }
    
    &.cancelled {
      background: #F44336;
      color: #fff;
    }
  }
  
  .guest-badge {
    display: inline-block;
    padding: 0.1rem 0.4rem;
    background: rgba(255, 193, 7, 0.2);
    color: #FFC107;
    border-radius: 4px;
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-left: 0.5rem;
  }
  
  .chat-info {
    flex: 1;
    min-width: 0;
    
    .chat-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 0.25rem;
      
      .chat-name {
        font-weight: ${props => props.active ? '700' : '600'};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        color: ${props => props.active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.95)'};
        flex: 1;
        margin-right: 0.5rem;
        font-size: 0.95rem;
        line-height: 1.3;
        font-family: ${props => props.theme?.fonts?.heading || 'inherit'};
        display: flex;
        align-items: center;
        transition: all 0.2s;
      }
      
      .transaction-amount {
        font-size: 0.8rem;
        color: ${props => props.active ? '#4CAF50' : '#4CAF50'};
        font-weight: 700;
        white-space: nowrap;
        font-family: ${props => props.theme?.fonts?.mono || 'SFMono-Regular, monospace'};
        letter-spacing: 0.3px;
      }
    }
    
    .chat-preview {
      font-size: 0.85rem;
      opacity: ${props => props.active ? '1' : '0.85'};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-bottom: 0.5rem;
      color: ${props => props.active ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.8)'};
      line-height: 1.3;
      font-weight: 400;
      transition: all 0.2s;
    }
    
    .chat-meta {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 0.75rem;
      
      .chat-time {
        opacity: ${props => props.active ? '0.9' : '0.7'};
        flex-shrink: 0;
        color: rgba(255, 255, 255, 0.7);
        font-family: ${props => props.theme?.fonts?.mono || 'SFMono-Regular, monospace'};
        font-weight: 400;
        letter-spacing: 0.2px;
      }
      
      .chat-status {
        display: flex;
        align-items: center;
        gap: 0.25rem;
        padding: 0.2rem 0.5rem;
        border-radius: 10px;
        font-size: 0.7rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.3px;
        font-family: ${props => props.theme?.fonts?.body || 'inherit'};
        
        &.pending {
          background: rgba(255, 193, 7, 0.2);
          color: #FFC107;
        }
        
        &.accepted {
          background: rgba(33, 150, 243, 0.2);
          color: #2196F3;
        }
        
        &.paid {
          background: rgba(156, 39, 176, 0.2);
          color: #9C27B0;
        }
        
        &.completed {
          background: rgba(76, 175, 80, 0.2);
          color: #4CAF50;
        }
        
        &.cancelled {
          background: rgba(244, 67, 54, 0.2);
          color: #F44336;
        }
      }
    }
  }
  
  .unread-badge {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    background: ${props => props.active ? '#A00000' : '#800000'};
    color: white;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    font-weight: 700;
    box-shadow: ${props => props.active ? 
      '0 3px 8px rgba(160, 0, 0, 0.5)' : 
      '0 2px 5px rgba(0, 0, 0, 0.2)'
    };
    font-family: ${props => props.theme?.fonts?.mono || 'SFMono-Regular, monospace'};
  }
  
  .delete-btn {
    position: absolute;
    bottom: 0.5rem;
    right: 0.5rem;
    background: transparent;
    border: none;
    color: #e74c3c;
    opacity: 0;
    transition: all 0.3s ease;
    cursor: pointer;
    padding: 0.25rem;
    transform: translateX(10px);
    border-radius: 4px;
    
    &:hover {
      transform: scale(1.2) translateX(0);
      background: rgba(231, 76, 60, 0.1);
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  margin: 2rem auto;
  border: 3px solid rgba(128, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #800000;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: rgba(255, 255, 255, 0.6);
  font-family: ${props => props.theme?.fonts?.body || 'inherit'};
  
  h3 {
    margin-bottom: 0.5rem;
    color: rgba(255, 255, 255, 0.8);
    font-size: 1.1rem;
    font-weight: 600;
    font-family: ${props => props.theme?.fonts?.heading || 'inherit'};
  }
  
  p {
    line-height: 1.5;
    font-size: 0.9rem;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.7);
  }
`;

const MessagesList = ({
  chats,
  selectedChat,
  selectedChatId,
  loading,
  searchTerm,
  onChatSelect,
  onDeleteChat,
  selectMode = false,
  selectedIds = new Set(),
  onToggleSelect,
}) => {
  const [profilePics, setProfilePics] = useState({});
  const [sellerOwnerNames, setSellerOwnerNames] = useState({});
  const [buyerEmails, setBuyerEmails] = useState({});
  const [buyerShopNames, setBuyerShopNames] = useState({});
  const [buyerShopPics, setBuyerShopPics] = useState({});
  // ✅ NEW: Track if user is guest
  const [isGuestUser, setIsGuestUser] = useState(false);

  // ✅ NEW: Check guest status on mount
  useEffect(() => {
    const checkGuestStatus = () => {
      const user = auth.currentUser;
      if (user && user.isAnonymous) {
        console.log('👤 User is guest (anonymous)');
        setIsGuestUser(true);
      } else {
        setIsGuestUser(false);
      }
    };
    
    checkGuestStatus();
    
    // Listen for auth changes
    const unsubscribe = auth.onAuthStateChanged(checkGuestStatus);
    return () => unsubscribe();
  }, []);

  // ✅ HELPER: Get current user's role
  const getCurrentUserRole = (chat) => {
    if (auth.currentUser) {
      const userId = auth.currentUser.uid;
      
      if (chat.roles && chat.roles[userId]) {
        return chat.roles[userId];
      }
      
      if (chat.buyerId === userId) return 'buyer';
      if (chat.sellerId === userId) return 'seller';
    }
    
    return null;
  };

  // ✅ NEW: Filter chats for guests (buyer-only)
  const filteredChats = useMemo(() => {
    if (!isGuestUser) return chats;
    
    console.log('🔍 Filtering chats for guest user');
    
    // For guests, only show chats where they are the buyer
    const guestChats = chats.filter(chat => {
      const userRole = getCurrentUserRole(chat);
      const isBuyer = userRole === 'buyer';
      
      if (!isBuyer) {
        console.log('⏭️ Hiding seller chat:', chat.id);
      }
      
      return isBuyer;
    });
    
    console.log(`✅ Showing ${guestChats.length} of ${chats.length} chats to guest`);
    return guestChats;
  }, [chats, isGuestUser]);

  // Fetch profile pics for both seller and buyer of every chat
  useEffect(() => {
    const fetchProfilePics = async () => {
      const pics = {};
      const ownerNames = {};
      const buyerEmailsLocal = {};
      const buyerShopNamesLocal = {};
      const buyerShopPicsLocal = {};
      const fetchedUserIds = new Set();

      for (const chat of filteredChats) {
        // --- Seller: shop profile pic → fallback users.photoURL; also grab display name ---
        if (chat.sellerId && !pics.hasOwnProperty(chat.sellerId)) {
          pics[chat.sellerId] = null; // mark as being fetched
          try {
            const shopSnap = await getDoc(doc(db, 'shops', chat.sellerId));
            if (shopSnap.exists() && shopSnap.data().profile) {
              pics[chat.sellerId] = shopSnap.data().profile;
            }
          } catch (_) {}

          if (!fetchedUserIds.has(chat.sellerId)) {
            fetchedUserIds.add(chat.sellerId);
            try {
              const userSnap = await getDoc(doc(db, 'users', chat.sellerId));
              if (userSnap.exists()) {
                const d = userSnap.data();
                if (!pics[chat.sellerId]) {
                  pics[chat.sellerId] = d.photoURL || d.profilePic || d.profile || null;
                }
                ownerNames[chat.sellerId] = d.displayName || d.firstName || null;
              }
            } catch (_) {}
          }
        }

        // --- Buyer email (skip guests) ---
        if (chat.buyerId && !chat.isGuestOrder && !buyerEmailsLocal.hasOwnProperty(chat.buyerId)) {
          buyerEmailsLocal[chat.buyerId] = null; // mark as being fetched
          if (!fetchedUserIds.has(chat.buyerId)) {
            fetchedUserIds.add(chat.buyerId);
            try {
              const userSnap = await getDoc(doc(db, 'users', chat.buyerId));
              if (userSnap.exists()) {
                buyerEmailsLocal[chat.buyerId] = userSnap.data().email || null;
              }
            } catch (_) {}
          }
        }

        // --- Buyer's own shop (name + shop pfp — used as-is, no personal-photo blending) ---
        if (chat.buyerId && !chat.isGuestOrder && !buyerShopNamesLocal.hasOwnProperty(chat.buyerId)) {
          buyerShopNamesLocal[chat.buyerId] = null;
          buyerShopPicsLocal[chat.buyerId] = null;
          try {
            const shopSnap = await getDoc(doc(db, 'shops', chat.buyerId));
            if (shopSnap.exists()) {
              const sd = shopSnap.data();
              buyerShopNamesLocal[chat.buyerId] = sd.name || null;
              buyerShopPicsLocal[chat.buyerId] = sd.profile || null;
            }
          } catch (_) {}
        }

      }

      setProfilePics(pics);
      setSellerOwnerNames(ownerNames);
      setBuyerEmails(buyerEmailsLocal);
      setBuyerShopNames(buyerShopNamesLocal);
      setBuyerShopPics(buyerShopPicsLocal);
    };

    if (filteredChats.length > 0) {
      fetchProfilePics();
    }
  }, [filteredChats]);
  
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      date = new Date(timestamp);
    }
    
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString();
    }
  };

  const getStatusInfo = (status, role) => {
    if (!status) return null;
    
    const statusMap = {
      pending_seller_acceptance: {
        label: role === 'buyer' ? 'Pending' : 'New Order',
        className: 'pending',
        icon: Clock
      },
      pending_send: {
        label: 'Cart',
        className: 'pending',
        icon: Package
      },
      seller_accepted: {
        label: role === 'buyer' ? 'Approved' : 'Approved',
        className: 'accepted',
        icon: Check
      },
      paid: {
        label: 'Paid',
        className: 'paid',
        icon: Package
      },
      completed: {
        label: 'Completed',
        className: 'completed',
        icon: Check
      },
      seller_rejected: {
        label: 'Declined',
        className: 'cancelled',
        icon: AlertCircle
      },
      withdrawn: {
        label: 'Withdrawn',
        className: 'cancelled',
        icon: AlertCircle
      }
    };
    
    return statusMap[status] || null;
  };

  const getTransactionAmount = (chat) => {
    const pendingPurchase = chat.pendingPurchase;
    if (!pendingPurchase) return null;
    
    if (pendingPurchase.finalTotalPrice) {
      return `$${pendingPurchase.finalTotalPrice.toFixed(2)}`;
    } else if (pendingPurchase.totalPrice) {
      return `$${pendingPurchase.totalPrice.toFixed(2)}`;
    } else if (chat.total) {
      return `$${chat.total.toFixed(2)}`;
    }
    
    return null;
  };

  const getStatusIndicator = (status) => {
    if (!status) return null;
    
    if (['pending_seller_acceptance', 'seller_accepted', 'paid', 'pending_send'].includes(status)) {
      return { className: 'active', icon: Clock };
    } else if (status === 'completed') {
      return { className: 'completed', icon: Check };
    } else if (['seller_rejected', 'withdrawn'].includes(status)) {
      return { className: 'cancelled', icon: AlertCircle };
    }
    
    return null;
  };

  const getItemCount = (chat) => {
    if (chat.cartItems && chat.cartItems.length > 0) {
      return chat.cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
    }
    
    if (chat.pendingPurchase) {
      const qty = chat.pendingPurchase.approvedQuantity || 
                  chat.pendingPurchase.requestedQuantity || 
                  chat.pendingPurchase.quantity || 1;
      return qty;
    }
    
    return 1;
  };

  if (loading) {
    return (
      <ChatItemsContainer>
        <LoadingSpinner />
        <p style={{ textAlign: 'center', opacity: 0.7 }}>
          {isGuestUser ? 'Loading your orders...' : 'Loading conversations...'}
        </p>
      </ChatItemsContainer>
    );
  }

  // ✅ CHANGED: Use filteredChats instead of chats
  if (filteredChats.length === 0) {
    return (
      <ChatItemsContainer>
        <EmptyMessage>
          <h3>
            {isGuestUser ? 'No orders found' : 'No conversations found'}
          </h3>
          <p>
            {searchTerm ? 
              `No results matching "${searchTerm}"` : 
              isGuestUser ?
                "You don't have any active orders yet" :
                "You don't have any conversations yet"
            }
          </p>
        </EmptyMessage>
      </ChatItemsContainer>
    );
  }

  return (
    <ChatItemsContainer>
      {/* ✅ CHANGED: Map over filteredChats */}
      {filteredChats.map(chat => {
        const currentUserRole = getCurrentUserRole(chat);
        
        const statusInfo = getStatusInfo(
          chat.transactionStatus || chat.status, 
          currentUserRole
        );
        const statusIndicator = getStatusIndicator(chat.transactionStatus || chat.status);
        const transactionAmount = getTransactionAmount(chat);
        const StatusIcon = statusInfo?.icon;
        const IndicatorIcon = statusIndicator?.icon;
        const itemCount = getItemCount(chat);
        
        const isActive = (selectedChat?.id === chat.id) || (selectedChatId === chat.id);
        
        const firstItemName = chat.cartItems?.[0]?.name || chat.itemName || '';
        const hasMultipleItems = itemCount > 1;

        let displayName = '';
        let displaySubtext = '';

        if (currentUserRole === 'buyer') {
          displayName = chat.sellerName || chat.otherPartyName || 'Unknown Shop';
          displaySubtext = hasMultipleItems ? `${itemCount} items` : (firstItemName || '1 item');
        } else if (currentUserRole === 'seller') {
          const buyerShopName = buyerShopNames[chat.buyerId];
          if (buyerShopName) {
            displayName = `Customer: ${buyerShopName}`;
          } else {
            const rawName = chat.buyerEmail || buyerEmails[chat.buyerId] || chat.buyerName || chat.otherPartyName || 'Unknown Buyer';
            const buyerDisplayName = rawName.includes('@') ? rawName.split('@')[0] : rawName;
            displayName = `Customer: ${buyerDisplayName}`;
          }
          // Same item name/count line the Buying tab already shows — this was
          // hardcoded to '' here, so Selling-tab rows never showed what was
          // actually being ordered underneath the buyer's name.
          displaySubtext = hasMultipleItems ? `${itemCount} items` : (firstItemName || '1 item');
        } else {
          displayName = chat.itemName || 'Unknown';
          displaySubtext = 'Order';
        }
        
        const isSellerRole = currentUserRole === 'seller';
        // Prefer pfp embedded in the chat doc (written at creation time, zero-delay).
        // Fall back to the async-fetched maps for older chats that predate the embed.
        const mainAvatarSrc = isSellerRole
          ? (!chat.isGuestOrder ? (chat.buyerShopPic || buyerShopPics[chat.buyerId] || null) : null)
          : (chat.sellerShopPic || profilePics[chat.sellerId] || null);

        const isSelected = selectedIds.has(chat.id);

        return (
          <ChatItem
            key={chat.id + (chat.selfOrderRole ? '_' + chat.selfOrderRole : '')}
            active={!selectMode && isActive}
            onClick={() => selectMode ? onToggleSelect?.(chat.id) : onChatSelect(chat)}
            isBuyer={chat.isBuyer}
            isSeller={chat.isSeller}
            style={selectMode && isSelected ? { border: '2px solid #60A5FA', background: 'rgba(96,165,250,0.12)' } : undefined}
          >
            {selectMode && (
              <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, color: isSelected ? '#60A5FA' : 'rgba(255,255,255,0.35)' }}>
                {isSelected ? <CheckSquare size={20} /> : <Square size={20} />}
              </div>
            )}

            <div className="chat-avatar">
              {mainAvatarSrc ? (
                <img
                  src={mainAvatarSrc}
                  alt={displayName}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <User size={24} />
              )}
            </div>

            <div className="chat-info">
              <div className="chat-header">
                <div className="chat-name">
                  {displayName}
                  {chat.isGuestOrder && (
                    <span className="guest-badge">Guest</span>
                  )}
                </div>
                {transactionAmount && (
                  <div className="transaction-amount">
                    {transactionAmount}
                  </div>
                )}
              </div>

              {displaySubtext && (
                <div className="chat-preview">
                  {displaySubtext}
                </div>
              )}

              <div className="chat-meta">
                <div className="chat-time">
                  {formatTimestamp(chat.lastMessageTime)}
                </div>

                {statusInfo && StatusIcon && (
                  <div className={`chat-status ${statusInfo.className}`}>
                    <StatusIcon size={10} />
                    {statusInfo.label}
                  </div>
                )}
              </div>
            </div>

            {!selectMode && chat.unreadCount > 0 && (
              <div className="unread-badge">
                {chat.unreadCount}
              </div>
            )}

            {!selectMode && (
              <button
                className="delete-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDeleteChat(chat.id, e);
                }}
                title="Delete conversation"
                aria-label="Delete conversation"
              >
                <Trash2 size={16} />
              </button>
            )}
          </ChatItem>
        );
      })}
    </ChatItemsContainer>
  );
};

export default MessagesList;